import React, { useRef, useState, useEffect } from 'react';
import { SparklesStrongIcon } from 'src/monet/icons';
import { trackSegmentEvent } from 'src/utils/segment';
import { Journey } from 'src/common/interfaces/journey.interface';
import { NeueCanvasButton } from 'src/editor/content-creation/components/neue-button/canvas';
import { PlayerAiAssistantPopover } from './ai-assistant-popover';
import { usePitchChatStore } from 'src/editor/content-creation/talk-to-journey/pitch-chat-store';
import { useEnhancedChatStore } from 'src/editor/content-creation/talk-to-journey/enhanced-store';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

interface PlayerAiAssistantButtonProps {
  journey: Journey;
}

export const PlayerAiAssistantButton = ({ journey }: PlayerAiAssistantButtonProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const buttonWrapperRef = useRef<HTMLDivElement>(null);
  const { features } = useFeatureRestrictions();
  
  const { disabled: pitchDisabled, initializeFromJourney: pitchInitializeFromJourney } = usePitchChatStore((state) => ({
    disabled: state.disabled,
    initializeFromJourney: state.initializeFromJourney,
  }));

  const { disabled: enhancedDisabled, initializeFromJourney: enhancedInitializeFromJourney } = useEnhancedChatStore((state) => ({
    disabled: state.disabled,
    initializeFromJourney: state.initializeFromJourney,
  }));

  // Use the appropriate store based on journey mode
  // In player mode, we should only check the journey's disabled setting, not feature restrictions
  const storeDisabled = journey.is_neue ? enhancedDisabled : pitchDisabled;
  const initializeFromJourney = journey.is_neue ? enhancedInitializeFromJourney : pitchInitializeFromJourney;
  
  // For player mode, we need to extract the actual journey disabled setting
  // The store's disabled state includes feature restrictions, but we only want the journey setting
  const journeyData = journey.data || {};
  const talkToJourneySettings = journeyData['talk_to_journey'] || {};
  const journeyDisabled = talkToJourneySettings.disabled || false;
  
  // In player mode, only respect the journey's disabled setting
  const disabled = journeyDisabled;

  // Initialize settings from journey data
  useEffect(() => {
    if (journey) {
      initializeFromJourney(journey);
    }
  }, [journey, initializeFromJourney]);

  const handleClick = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
  };

  // Don't render the button if chatbot is disabled
  if (disabled) {
    return null;
  }

  return (
    <div className="relative" ref={buttonWrapperRef}>
      <NeueCanvasButton
        className="py-[5px] px-2.5 !bg-white/90 !text-gray-700 backdrop-blur-sm border border-gray-200 shadow-sm hover:!bg-white hover:!text-gray-900"
        type="button"
        onClick={handleClick}
      >
        <div className="flex items-center space-x-[6px]">
          <span>Ask anything</span>
          <SparklesStrongIcon />
        </div>
      </NeueCanvasButton>
      <PlayerAiAssistantPopover
        isOpen={isPopoverOpen}
        onClose={handleClose}
        journey={journey}
        buttonRef={buttonWrapperRef}
      />
    </div>
  );
}; 